import { useEffect, useRef, type FunctionComponent } from 'react';
import type { DefaultPrivacyLevel } from '@datadog/browser-rum-slim';
import { useTenantConfig } from '@/contexts/TenantConfig';
import { useAuthentication } from '@/contexts/Authentication';
import useDatadogRum from '@/hooks/useDatadogRum';

// Extend the Window interface to include DD_RUM global
declare global {
  interface Window {
    DD_RUM?: {};
  }
}

export interface RealUserMetricsProps {
  applicationId: string;
  clientToken: string;
  serviceName: string;
  environment: string;
  tracingUrls?: string[];
  options?: {
    version?: string;
    sampleRate?: number;
    replaySampleRate?: number;
    trackUserInteractions?: boolean;
    trackResources?: boolean;
    trackLongTasks?: boolean;
    defaultPrivacyLevel?: DefaultPrivacyLevel;
  };
}

const RealUserMetrics: FunctionComponent<RealUserMetricsProps> = ({
  applicationId,
  clientToken,
  serviceName,
  environment,
  tracingUrls = [],
  options,
}) => {
  const initialized = useRef(false);
  const { id, slug } = useTenantConfig();
  const { authUser } = useAuthentication();
  const { rum } = useDatadogRum();

  useEffect(() => {
    rum((dd, { DefaultPrivacyLevel }) => {
      // Don't initialize twice:
      if (initialized.current === true) {
        return;
      }
      const fullOptions = {
        sampleRate: 100,
        replaySampleRate: 20,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: DefaultPrivacyLevel.MASK_USER_INPUT,
        actionNameAttribute: 'data-action-name',
        ...options,
        ...(tracingUrls.length > 0 ? { allowedTracingUrls: tracingUrls } : {}),
      };

      dd.init({
        applicationId: applicationId,
        clientToken: clientToken,
        site: 'datadoghq.com',
        service: serviceName,
        version: process.env.GIT_SHA,
        env: environment,
        ...fullOptions,
      });

      if (authUser) {
        dd.setUser({
          id: authUser?.patientId ?? authUser?.staffId ?? '',
        });
      }

      // we set the current tenant/customer identification as the datadog 'account' prop
      dd.setAccount({
        id,
        name: slug,
      });

      initialized.current = true;
    });
  }, [
    applicationId,
    authUser,
    clientToken,
    environment,
    id,
    slug,
    options,
    rum,
    serviceName,
    tracingUrls,
  ]);

  // user session change
  useEffect(() => {
    rum((dd) => {
      if (authUser) {
        dd.setUser({
          id: authUser?.patientId ?? authUser?.staffId ?? '',
        });
        return;
      }
      dd.clearUser();
    });
  }, [authUser, rum]);

  return null;
};

export default RealUserMetrics;
